import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  FormGroup,
  Checkbox,
  Stack
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Preview,
  Analytics,
  DragIndicator,
  Remove,
  ContentCopy,
  Visibility
} from '@mui/icons-material';
import api from '../services/api';

interface FormField {
  id: string;
  type: string;
  label: string;
  placeholder?: string;
  required: boolean;
  options?: string[];
  validation?: Record<string, any>;
}

interface PreChatForm {
  id: number;
  title: string;
  subtitle: string;
  fields: FormField[];
  channel_id?: number;
  is_enabled: boolean;
  is_required: boolean;
  show_message_box: boolean;
  logo_url?: string;
  primary_color: string;
  enable_business_hours_check: boolean;
  offline_message: string;
  total_submissions: number;
  conversion_rate: number;
  created_at: string;
}

interface FormSubmission {
  id: number;
  form_data: Record<string, any>;
  initial_message?: string;
  submitted_at: string;
  contact_id?: number;
  conversation_id?: number;
}

const FIELD_TYPES = [
  { value: 'text', label: 'Text Input' },
  { value: 'email', label: 'Email' },
  { value: 'tel', label: 'Phone Number' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'select', label: 'Dropdown' },
  { value: 'checkbox', label: 'Checkbox' }
];

const PreChatFormsPage: React.FC = () => {
  const [forms, setForms] = useState<PreChatForm[]>([]);
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [predefinedFields, setPredefinedFields] = useState<Record<string, any>>({});
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState<PreChatForm | null>(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(true);

  // Form state
  const [formData, setFormData] = useState({
    title: "Let's get you connected!",
    subtitle: "Please provide some details to help us assist you better.",
    fields: [] as FormField[],
    is_enabled: true,
    is_required: false,
    show_message_box: true,
    primary_color: "#1f93ff",
    enable_business_hours_check: false,
    offline_message: "We're currently offline. Please leave your details and we'll get back to you."
  });

  const [newField, setNewField] = useState<FormField>({
    id: '',
    type: 'text',
    label: '',
    placeholder: '',
    required: false,
    options: [],
    validation: {}
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch forms
      const formsResponse = await api.get('/pre-chat-forms/');
      setForms(formsResponse.data || []);

      // Fetch predefined fields
      const fieldsResponse = await api.get('/pre-chat-forms/public/predefined-fields');
      setPredefinedFields(fieldsResponse.data.fields || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateForm = async () => {
    try {
      if (selectedForm) {
        await api.put(`/pre-chat-forms/${selectedForm.id}`, formData);
      } else {
        await api.post('/pre-chat-forms/', formData);
      }
      
      setIsFormDialogOpen(false);
      setSelectedForm(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving form:', error);
    }
  };

  const handleEditForm = (form: PreChatForm) => {
    setSelectedForm(form);
    setFormData({
      title: form.title,
      subtitle: form.subtitle,
      fields: form.fields,
      is_enabled: form.is_enabled,
      is_required: form.is_required,
      show_message_box: form.show_message_box,
      primary_color: form.primary_color,
      enable_business_hours_check: form.enable_business_hours_check,
      offline_message: form.offline_message
    });
    setIsFormDialogOpen(true);
  };

  const handleDeleteForm = async (formId: number) => {
    if (window.confirm('Are you sure you want to delete this form?')) {
      try {
        await api.delete(`/pre-chat-forms/${formId}`);
        fetchData();
      } catch (error) {
        console.error('Error deleting form:', error);
      }
    }
  };

  const handleAddField = () => {
    if (!newField.label || !newField.id) return;
    
    setFormData(prev => ({
      ...prev,
      fields: [...prev.fields, { ...newField }]
    }));
    
    setNewField({
      id: '',
      type: 'text',
      label: '',
      placeholder: '',
      required: false,
      options: [],
      validation: {}
    });
  };

  const handleRemoveField = (index: number) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index)
    }));
  };

  const handleAddPredefinedField = (fieldKey: string) => {
    const field = predefinedFields[fieldKey];
    if (field) {
      const newFieldData: FormField = {
        id: fieldKey,
        type: field.type,
        label: field.label,
        placeholder: field.placeholder || '',
        required: field.required || false,
        options: field.options || [],
        validation: field.validation || {}
      };
      
      setFormData(prev => ({
        ...prev,
        fields: [...prev.fields, newFieldData]
      }));
    }
  };

  const fetchSubmissions = async (formId: number) => {
    try {
      const response = await api.get(`/pre-chat-forms/${formId}/submissions`);
      setSubmissions(response.data.submissions || []);
      setCurrentTab(1); // Switch to Submissions tab to show the data
    } catch (error) {
      console.error('Error fetching submissions:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "Let's get you connected!",
      subtitle: "Please provide some details to help us assist you better.",
      fields: [],
      is_enabled: true,
      is_required: false,
      show_message_box: true,
      primary_color: "#1f93ff",
      enable_business_hours_check: false,
      offline_message: "We're currently offline. Please leave your details and we'll get back to you."
    });
  };

  const PreviewForm = ({ form }: { form: typeof formData }) => (
    <Card sx={{ maxWidth: 400, mx: 'auto' }}>
      <CardHeader
        title={form.title}
        subheader={form.subtitle}
        sx={{ 
          backgroundColor: form.primary_color,
          color: 'white',
          '& .MuiCardHeader-subheader': { color: 'rgba(255,255,255,0.8)' }
        }}
      />
      <CardContent>
        <Stack spacing={2}>
          {form.fields.map((field, index) => (
            <Box key={index}>
              {field.type === 'text' || field.type === 'email' || field.type === 'tel' ? (
                <TextField
                  fullWidth
                  label={field.label}
                  placeholder={field.placeholder}
                  required={field.required}
                  type={field.type}
                  size="small"
                />
              ) : field.type === 'textarea' ? (
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label={field.label}
                  placeholder={field.placeholder}
                  required={field.required}
                  size="small"
                />
              ) : field.type === 'select' ? (
                <FormControl fullWidth size="small">
                  <InputLabel>{field.label}</InputLabel>
                  <Select label={field.label}>
                    {field.options?.map((option, idx) => (
                      <MenuItem key={idx} value={option}>{option}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : field.type === 'checkbox' ? (
                <FormControlLabel
                  control={<Checkbox />}
                  label={field.label}
                />
              ) : null}
            </Box>
          ))}
          
          {form.show_message_box && (
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Your Message"
              placeholder="How can we help you today?"
              size="small"
            />
          )}
          
          <Button variant="contained" sx={{ backgroundColor: form.primary_color }}>
            Start Conversation
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Pre-Chat Forms
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setIsFormDialogOpen(true)}
        >
          Create Form
        </Button>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)}>
          <Tab label="Forms" />
          <Tab label="Submissions" />
        </Tabs>
      </Box>

      {/* Forms Tab */}
      {currentTab === 0 && (
        <Grid container spacing={3}>
          {forms.map((form) => (
            <Grid item xs={12} md={6} lg={4} key={form.id}>
              <Card>
                <CardHeader
                  title={form.title}
                  subheader={`${form.fields.length} fields`}
                  action={
                    <Chip
                      label={form.is_enabled ? 'Active' : 'Disabled'}
                      color={form.is_enabled ? 'success' : 'default'}
                      size="small"
                    />
                  }
                />
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {form.subtitle}
                  </Typography>
                  
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      Submissions: {form.total_submissions}
                    </Typography>
                    <Typography variant="body2">
                      Conversion Rate: {form.conversion_rate}%
                    </Typography>
                  </Box>
                  
                  {form.total_submissions > 0 && (
                    <LinearProgress
                      variant="determinate"
                      value={form.conversion_rate}
                      sx={{ mt: 1, height: 6, borderRadius: 3 }}
                    />
                  )}
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<Edit />}
                    onClick={() => handleEditForm(form)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    startIcon={<Visibility />}
                    onClick={() => {
                      setFormData(form);
                      setIsPreviewOpen(true);
                    }}
                  >
                    Preview
                  </Button>
                  <Button
                    size="small"
                    startIcon={<Analytics />}
                    onClick={() => fetchSubmissions(form.id)}
                  >
                    View Data
                  </Button>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteForm(form.id)}
                    color="error"
                  >
                    <Delete />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Submissions Tab */}
      {currentTab === 1 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Submission Date</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Form Data</TableCell>
                <TableCell>Initial Message</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {submissions.map((submission) => (
                <TableRow key={submission.id}>
                  <TableCell>
                    {new Date(submission.submitted_at).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {submission.form_data.name || submission.form_data.email || 'Anonymous'}
                  </TableCell>
                  <TableCell>
                    {Object.entries(submission.form_data).map(([key, value]) => (
                      <Chip key={key} label={`${key}: ${value}`} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                    ))}
                  </TableCell>
                  <TableCell>
                    {submission.initial_message ? (
                      <Tooltip title={submission.initial_message}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {submission.initial_message}
                        </Typography>
                      </Tooltip>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {submission.conversation_id ? (
                      <Button size="small">View Conversation</Button>
                    ) : (
                      <Button size="small">Create Conversation</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Form Dialog */}
      <Dialog open={isFormDialogOpen} onClose={() => setIsFormDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedForm ? 'Edit Pre-Chat Form' : 'Create Pre-Chat Form'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Form Title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Primary Color"
                  type="color"
                  value={formData.primary_color}
                  onChange={(e) => setFormData(prev => ({ ...prev, primary_color: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Subtitle"
                  value={formData.subtitle}
                  onChange={(e) => setFormData(prev => ({ ...prev, subtitle: e.target.value }))}
                />
              </Grid>
              
              {/* Form Settings */}
              <Grid item xs={12}>
                <FormGroup row>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_enabled}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_enabled: e.target.checked }))}
                      />
                    }
                    label="Enable Form"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_required}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_required: e.target.checked }))}
                      />
                    }
                    label="Required Form"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.show_message_box}
                        onChange={(e) => setFormData(prev => ({ ...prev, show_message_box: e.target.checked }))}
                      />
                    }
                    label="Show Message Box"
                  />
                </FormGroup>
              </Grid>

              {/* Predefined Fields */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Quick Add Fields
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {Object.keys(predefinedFields).map((key) => (
                    <Button
                      key={key}
                      size="small"
                      variant="outlined"
                      onClick={() => handleAddPredefinedField(key)}
                    >
                      Add {predefinedFields[key].label}
                    </Button>
                  ))}
                </Box>
              </Grid>

              {/* Custom Field Builder */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Add Custom Field
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="Field ID"
                      value={newField.id}
                      onChange={(e) => setNewField(prev => ({ ...prev, id: e.target.value }))}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={newField.type}
                        onChange={(e) => setNewField(prev => ({ ...prev, type: e.target.value }))}
                      >
                        {FIELD_TYPES.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Label"
                      value={newField.label}
                      onChange={(e) => setNewField(prev => ({ ...prev, label: e.target.value }))}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button onClick={handleAddField} variant="contained" size="small">
                      Add
                    </Button>
                  </Grid>
                </Grid>
              </Grid>

              {/* Form Fields List */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Form Fields ({formData.fields.length})
                </Typography>
                <List>
                  {formData.fields.map((field, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={field.label}
                        secondary={`${field.type} ${field.required ? '(Required)' : ''}`}
                      />
                      <ListItemSecondaryAction>
                        <IconButton onClick={() => handleRemoveField(index)}>
                          <Remove />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsFormDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => {
            setFormData(formData);
            setIsPreviewOpen(true);
          }}>
            Preview
          </Button>
          <Button onClick={handleCreateForm} variant="contained">
            {selectedForm ? 'Update' : 'Create'} Form
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={isPreviewOpen} onClose={() => setIsPreviewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Form Preview</DialogTitle>
        <DialogContent>
          <PreviewForm form={formData} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PreChatFormsPage;